/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useMemo, useRef } from "react";

import cn from "classnames";
import useStateRef from "react-usestateref";

import { verifyLettersNumbersAndEmail } from "utils/validator";

import MoreContentDropDown from "components/common/MoreContentDropDown";
import { SearchIcon } from "components/icons/common";
import SessionsSearchIcon from "components/icons/SessionsSearchIcon";

import { Input, RadioGroup } from "../Input";
import { Spinner } from "../Loaders";
import Stack from "../Stack";
import Text from "../Text";

interface ISearchBar {
  children?: React.ReactNode;
  onSearch: (...args: any) => void;
  isLoading?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  placeholder?: string;
  isActive?: boolean;
  preferences?: { label: string; value: string }[];
  onPreference?: (...args: any) => void;
  searchWithSpecialCharacters?: boolean;
  searchInputClassName?: string;
  containerClassName?: string;
  searchIconColor?: string;
}

const SearchBar = ({
  placeholder = "Search...",
  children,
  onSearch,
  isLoading,
  defaultValue,
  disabled,
  isActive,
  preferences,
  onPreference,
  searchWithSpecialCharacters = false,
  searchInputClassName,
  containerClassName,
  searchIconColor = "#535353",
  ...rest
}: ISearchBar) => {
  const inputRef = useRef<any>(null);
  useEffect(() => {
    if (!isLoading) inputRef.current?.focus();
  }, [isLoading]);

  const augmentedPreferences = useMemo(
    () =>
      preferences
        ? preferences?.map((pref) => ({ ...pref, id: pref.value }))
        : null,
    [],
  );

  const [, setValue, { current: value }] = useStateRef(defaultValue || "");
  useEffect(() => {
    setValue(defaultValue || "");
  }, [defaultValue]);

  const timeoutRef = useRef<any>(null);
  return (
    <>
      <div
        className={cn(
          "group focus-within:!border-[#838383] focus-within:!divide-[#838383] focus:ring-0 focus:outline-none flex relative border-y-2 border-r-2 divide-x-2 border-c-gray-600 divide-c-gray-600",
          {
            "divide-warning": isActive,
          },
          containerClassName,
        )}
      >
        <div className=" text-white absolute top-3 left-3" test-id="5KjHItmY">
          {isLoading ? (
            <Spinner size="xs" test-id="Mm6pUIDU" />
          ) : (
            <SearchIcon color={searchIconColor} test-id="Rs70Udhw" />
          )}
        </div>

        <Input
          ref={inputRef}
          className={cn(
            "pl-10 h-10 border-none focus:outline-none focus:ring-0 sm:w-80",
            searchInputClassName,
          )}
          placeholder={placeholder}
          onChange={(e) => {
            if (searchWithSpecialCharacters)
              return setValue(e.target.value?.trimStart());

            setValue(verifyLettersNumbersAndEmail(e.target.value?.trimStart()));
          }}
          // Add value to empty the input if i delete the value
          value={value}
          disabled={isLoading || disabled}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }

            if (e.key === "Enter") return onSearch(value);

            if (e.key === "Escape") {
              setValue("");
              return onSearch(""); // newly added... Not sure 100%
            }

            timeoutRef.current = setTimeout(() => {
              onSearch(value);
            }, 1000);
          }}
          {...rest}
          test-id="oQFhPsrr"
        />

        {children}

        {augmentedPreferences && (
          <MoreContentDropDown
            anchorProps={{
              className: cn(
                "!bg-transparent w-[40px] p-0 !min-h-[40px] max-h-[45px] border-none",
                {
                  "pointer-events-none opacity-60": isLoading || disabled,
                },
              ),
              children: <SessionsSearchIcon />,
            }}
            className={cn(
              "whitespace-nowrap group-focus-within:border-[#838383]",
            )}
            scrollable={false}
          >
            <Stack className="space-y-s5 py-3 pl-2 pr-4">
              <Text as="span" className="text-sm text-[#DDDDDD]">
                Search Preferences
              </Text>

              <RadioGroup
                className="pl-2"
                options={augmentedPreferences}
                name="search preferences"
                onChange={onPreference!}
                labelArgs={{
                  className: "text-sm text-[#DDDDDD] !font-normal",
                }}
              />
            </Stack>
          </MoreContentDropDown>
        )}
      </div>
    </>
  );
};

export default SearchBar;
